/**
 * How well each taxonomy verb suits a framework level, shown as a rated
 * verb chip and the legend that keys it.
 * @module components/ui/VerbSuitability
 */

import type { VerbSuitability } from "@/taxonomy/strategy";
import type { TaxonomyLevel, VerbEntry } from "@/taxonomy/types";

import { Badge, type BadgeProps } from "./Badge";
import { Icon } from "./Icon";
import { Tooltip } from "./Tooltip";

/** How each suitability reads: the icon carries it without colour. */
const SUITABILITY: Record<
  VerbSuitability,
  {
    label: string;
    icon: string;
    tone: BadgeProps["tone"];
    variant: BadgeProps["variant"];
    className: string;
  }
> = {
  recommended: {
    label: "Recommended",
    icon: "check-circle",
    tone: "success",
    variant: "soft",
    className: "gap-1",
  },
  possible: {
    label: "Possible",
    icon: "circle-half",
    tone: "primary",
    variant: "outline",
    className: "gap-1",
  },
  unlikely: {
    label: "Unlikely",
    icon: "minus-circle",
    tone: "neutral",
    variant: "outline",
    className: "gap-1 text-muted",
  },
};

const SUITABILITY_ORDER: readonly VerbSuitability[] = ["recommended", "possible", "unlikely"];

/** Names of the given levels the verb sits at, as in "Analyse and Evaluate". */
function levelsHolding(verb: VerbEntry, levels: readonly TaxonomyLevel[], ids: readonly string[]) {
  const names = levels
    .filter((l) => ids.includes(l.id) && l.verbs.some((v) => v.lemma === verb.lemma))
    .map((l) => l.name);
  return { names: names.join(" and "), plural: names.length > 1 };
}

/** Why a verb got its rating, naming the level behind it. */
function suitabilityTip(
  verb: VerbEntry,
  suitability: VerbSuitability,
  where: string,
  levels: readonly TaxonomyLevel[],
  primary: readonly string[],
  secondary: readonly string[],
): string {
  const { label } = SUITABILITY[suitability];
  if (suitability === "unlikely") {
    return `${label} for ${where}: not emphasised at this level, but fine where the outcome calls for it.`;
  }
  const held = levelsHolding(verb, levels, suitability === "recommended" ? primary : secondary);
  const focus = suitability === "recommended" ? "a primary focus" : "a supporting level";
  return `${label} for ${where}: ${held.names} ${held.plural ? "are each" : "is"} ${focus}.`;
}

export interface VerbBadgeProps {
  verb: VerbEntry;
  /** Null leaves the verb unrated, as when no framework level is set. */
  suitability: VerbSuitability | null;
  /** The framework level, as in "NFQ 8". */
  where: string;
  levels: readonly TaxonomyLevel[];
  primary: readonly string[];
  secondary: readonly string[];
}

/** A verb chip carrying its suitability as icon, hidden text and tooltip. */
export function VerbBadge({
  verb,
  suitability,
  where,
  levels,
  primary,
  secondary,
}: VerbBadgeProps) {
  if (!suitability) {
    return (
      <Badge tone="neutral" variant="outline" size="xs">
        {verb.lemma}
      </Badge>
    );
  }
  const look = SUITABILITY[suitability];
  return (
    <Tooltip tip={suitabilityTip(verb, suitability, where, levels, primary, secondary)} fromSm>
      <Badge tone={look.tone} variant={look.variant} size="xs" className={look.className}>
        <Icon name={look.icon} aria-hidden />
        <span data-suitability={suitability}>{verb.lemma}</span>
        <span className="sr-only">
          , {look.label.toLowerCase()} for {where}
        </span>
      </Badge>
    </Tooltip>
  );
}

/** Keys the level tints and verb ratings, and says the guidance is advisory. */
export function SuitabilityLegend({ where, testId }: { where: string; testId: string }) {
  return (
    <div className="flex flex-col gap-2 mb-3 text-xs text-muted" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-semibold text-base-content">Levels</span>
        <span>
          <span className="inline-block w-3 h-3 rounded border border-primary-strong bg-primary/30 mr-1 align-middle" />
          Primary focus for {where}
        </span>
        <span>
          <span className="inline-block w-3 h-3 rounded border border-secondary mr-1 align-middle" />
          Secondary focus: supporting levels
        </span>
        <span>
          <span className="inline-block w-3 h-3 rounded border border-border-strong mr-1 align-middle" />
          No highlight: not emphasised at this level
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-semibold text-base-content">Verbs</span>
        {SUITABILITY_ORDER.map((key) => {
          const look = SUITABILITY[key];
          return (
            <span key={key} className="inline-flex items-center gap-1">
              <Badge tone={look.tone} variant={look.variant} size="xs" className={look.className}>
                <Icon name={look.icon} aria-hidden />
                {look.label}
              </Badge>
              {key === "recommended" && "sits at a primary focus level"}
              {key === "possible" && "sits at a supporting level"}
              {key === "unlikely" && "not emphasised here"}
            </span>
          );
        })}
      </div>
      <span>
        This guidance is advisory and never blocks progress. Any verb can be right when the outcome
        calls for it.
      </span>
    </div>
  );
}
